import type {
    LoginRequest,
    LoginResponse,
    PlaylistItemRequest,
    SendItemResultRequest,
    SendStateRequest,
    SendTrackingRequest,
} from './index';
import type { Playlist, PlaylistItemContent } from '../playlist';
import env from '../env';

type RuntimePlaylist = {
    name: string;
    description: string | null;
    resolution: string | null;
    template_name: string | null;
    template_url: string | null;
    logo?: Playlist['logo'];
    display_safearea: boolean;
    map_detail?: string;
    audio_url: string | null;
    sse_enabled?: boolean;
    playlist_revision: string;
    playlist: {
        resource_id: string;
        resource_type: string;
        playback_sec: number;
        enable_audio?: boolean | null;
    }[];
};

const contentTypes = {
    'camera:live': 'live',
    'camera:latest_recording': 'live_recording',
    'camera:latest_timelapse': 'live_timelapse',
    video: 'video',
    'video_importer:location_latest': 'video_importer',
} as const;

export class ApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.status = status;
    }
}

export class RemoteClient {
    login: LoginRequest;
    accessToken = '';
    private tokenExpiresAt = 0;
    private tokenRequest: Promise<void> | null = null;
    private runtime: RuntimePlaylist | null = null;

    constructor(login: LoginRequest) {
        this.login = login;
    }

    private get runtimeBase(): string {
        return `${env.internalApiUrl}/customers/${encodeURIComponent(this.login.cid)}/playouts/${encodeURIComponent(this.login.pid)}`;
    }

    // Sends the request with a valid token and renews it once on 401.
    private async authorizedFetch(
        url: string,
        init: RequestInit,
    ): Promise<Response> {
        for (let attempt = 0; attempt < 2; attempt++) {
            if (!this.accessToken || Date.now() >= this.tokenExpiresAt)
                await this.fetchToken();
            const headers = new Headers(init.headers);
            headers.set('authorization', `Bearer ${this.accessToken}`);
            const response = await fetch(url, {
                ...init,
                headers,
                cache: 'no-store',
            });
            if (response.status === 401 && attempt === 0) {
                this.accessToken = '';
                continue;
            }
            return response;
        }
        throw new Error('Playout authentication failed');
    }

    async fetch(url: string, init: RequestInit): Promise<any> {
        const response = await this.authorizedFetch(url, init);
        if (!response.ok)
            throw new ApiError(
                `Playout API request failed (HTTP ${response.status})`,
                response.status,
            );
        return response.status === 204 ? null : response.json();
    }

    // The command stream needs the Authorization header, which EventSource
    // cannot send, so it is read as a streaming fetch.
    async openCommandStream(
        lastEventId: string | null,
        signal: AbortSignal,
    ): Promise<ReadableStream<Uint8Array>> {
        const headers: Record<string, string> = {
            accept: 'text/event-stream',
        };
        if (lastEventId) headers['last-event-id'] = lastEventId;
        const response = await this.authorizedFetch(
            `${this.runtimeBase}/runtime/events`,
            { method: 'GET', headers, signal },
        );
        if (!response.ok || !response.body)
            throw new ApiError(
                `Playout event stream failed (HTTP ${response.status})`,
                response.status,
            );
        return response.body;
    }

    async fetchToken(): Promise<void> {
        if (this.tokenRequest) return this.tokenRequest;
        this.tokenRequest = this.requestToken();
        try {
            await this.tokenRequest;
        } finally {
            this.tokenRequest = null;
        }
    }

    private async requestToken(): Promise<void> {
        if (
            !this.login.user ||
            !this.login.pass ||
            !this.login.cid ||
            !this.login.pid
        )
            throw new Error('Missing playout credentials');
        const response = await fetch(env.oauthTokenUrl, {
            method: 'POST',
            cache: 'no-store',
            headers: {
                accept: 'application/json',
                'content-type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                grant_type: 'client_credentials',
                client_id: this.login.user,
                client_secret: this.login.pass,
                resource: 'internal_api',
                scope: 'internal_playout:create',
            }).toString(),
        });
        if (!response.ok)
            throw new Error(
                `Playout authentication failed (HTTP ${response.status})`,
            );
        const data = (await response.json()) as LoginResponse;
        if (
            !data.access_token ||
            !Number.isFinite(data.expires_in) ||
            data.expires_in <= 0
        )
            throw new Error('Invalid OAuth token response');
        this.accessToken = data.access_token;
        this.tokenExpiresAt =
            Date.now() + Math.max(1, data.expires_in - 30) * 1000;
    }

    async fetchPlaylist(): Promise<Playlist> {
        const runtime = (await this.fetch(`${this.runtimeBase}/runtime`, {
            method: 'GET',
        })) as RuntimePlaylist;
        const playlist = runtime.playlist.map((item) => {
            const type =
                contentTypes[item.resource_type as keyof typeof contentTypes];
            if (!type)
                throw new Error(
                    `Unsupported playlist resource type: ${item.resource_type}`,
                );
            return {
                playlist_revision: runtime.playlist_revision,
                content_id: item.resource_id,
                content_type: type,
                content_url: null,
                playback_sec: item.playback_sec,
                enable_audio: item.enable_audio === true,
            };
        });
        this.runtime = runtime;
        return {
            name: runtime.name,
            description: runtime.description || '',
            resolution: Number(runtime.resolution) || 1080,
            template_name: runtime.template_url || runtime.template_name || '',
            logo: runtime.logo,
            display_safearea: runtime.display_safearea,
            map_detail: runtime.map_detail,
            playlist,
            playlist_revision: runtime.playlist_revision,
            audio_url: runtime.audio_url || '',
            sse_enabled: runtime.sse_enabled === true,
        };
    }

    async getContent(
        request: PlaylistItemRequest,
    ): Promise<PlaylistItemContent> {
        if (!this.runtime)
            throw new Error('Load the playlist before resolving content');
        const revision =
            request.playlist_revision || this.runtime.playlist_revision;
        const data = await this.fetch(
            `${this.runtimeBase}/runtime/playlist/${request.index}?${new URLSearchParams({ playlist_revision: revision })}`,
            { method: 'GET' },
        );
        const meta = data.meta || {};
        const forecast = data.weather?.forecast || [];
        const days = [
            'Sonntag',
            'Montag',
            'Dienstag',
            'Mittwoch',
            'Donnerstag',
            'Freitag',
            'Samstag',
        ];
        const day = new Date().getDay();
        let website = '';
        try {
            website = new URL(meta.website).hostname;
        } catch {
            /* Optional metadata. */
        }
        const metadata: Record<string, any> = {
            content_id: data.resource_id,
            index: request.index,
            camera_id: data.camera_id,
            location_name: meta.name_short || meta.name || '',
            location_producer: meta.producer || 'livespotting.com',
            location_banner: meta.logo_light_url || meta.logo_url || '',
            logo_background_color:
                meta.logo_light_background_color ||
                meta.logo_background_color ||
                '#ffffff',
            location_website: website,
            location_today: days[day],
            longitude: meta.longitude ?? 0,
            latitude: meta.latitude ?? 0,
            country_code: meta.country_code || '',
            location_country_code: meta.country_code || '',
        };
        for (const key of ['city', 'county', 'region', 'country'])
            metadata[`location_${key}`] = meta[key] || '';
        [36, 60, 84].forEach((hour, index) => {
            metadata[`location_weather_icon${hour}`] =
                forecast[index]?.icon || '';
            metadata[`location_weather_tmax${hour}`] =
                forecast[index]?.temperature?.max ?? '';
            metadata[`location_weather_tmin${hour}`] =
                forecast[index]?.temperature?.min ?? '';
            metadata[`location_weather_day${index + 1}`] = days[
                (day + index) % 7
            ].substring(0, 2);
        });
        return {
            // Imports are tracked by their configured location, as before the
            // Internal API, not by the resolved imported video.
            id:
                request.content_type === 'video_importer'
                    ? request.content_id
                    : data.resource_id,
            type: request.content_type,
            camera_id: data.camera_id,
            index: request.index,
            playlist_revision: revision,
            metadata,
            source: { src: data.source_url, type: data.mimetype },
            playback_sec: request.playback_sec,
            enable_audio: request.enable_audio,
        };
    }

    // While paused no slot timer runs, so the cursor has no expected end.
    async sendState(request: SendStateRequest): Promise<any> {
        if (
            request.position === undefined ||
            !request.playlist_revision ||
            (!request.paused && !request.expected_ends_at)
        )
            return null;
        try {
            return await this.fetch(`${this.runtimeBase}/playlist-state`, {
                method: 'PUT',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    state: request.paused ? 'paused' : 'playing',
                    playlist_revision: request.playlist_revision,
                    active_playlist_index: request.position,
                    active_started_at: request.started_at,
                    expected_ends_at: request.paused
                        ? null
                        : request.expected_ends_at,
                    observed_at: new Date().toISOString(),
                }),
            });
        } catch (error) {
            console.error('Could not publish playlist state', error);
            return null;
        }
    }

    // Reported without a cursor, so a failed preload never replaces the item
    // currently on screen. Not retried: a 400 means the revision is outdated,
    // and the next content request reloads the playlist (409) anyway.
    async sendItemResult(request: SendItemResultRequest): Promise<any> {
        if (!request.playlist_revision) return null;
        const item_result: Record<string, unknown> = {
            index: request.index,
            status: request.status,
        };
        if (request.status === 'failed')
            item_result.reason_code = request.reason_code;
        console.log(
            `item result index=${request.index} ${request.status}${request.reason_code ? ` ${request.reason_code}` : ''} at ${request.observed_at}`,
        );
        try {
            return await this.fetch(`${this.runtimeBase}/playlist-state`, {
                method: 'PUT',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    playlist_revision: request.playlist_revision,
                    observed_at: request.observed_at,
                    item_result,
                }),
            });
        } catch (error) {
            console.error('Could not publish item result', error);
            return null;
        }
    }

    async sendTracking(request: SendTrackingRequest): Promise<any> {
        if (
            !request.content_id ||
            !request.start_date ||
            !request.end_date ||
            request.end_date <= request.start_date
        ) {
            console.warn('tracking skipped: incomplete request', request);
            return null;
        }
        const fields: Record<string, string> = {
            live: 'camera_id',
            live_recording: 'camera_recording_id',
            live_timelapse: 'camera_timelapse_id',
            video: 'video_id',
            video_importer: 'video_importer_id',
        };
        const field = fields[request.content_type || ''];
        if (!field) {
            console.warn(
                `tracking skipped: unsupported content type ${request.content_type}`,
            );
            return null;
        }
        const ids: Record<string, string> = { [field]: request.content_id };
        // Recordings and timelapses also name the camera they belong to.
        if (request.camera_id && field !== 'camera_id')
            ids.camera_id = request.camera_id;
        const seconds =
            (Date.parse(request.end_date) - Date.parse(request.start_date)) /
            1000;
        console.log(
            `tracking ${Object.entries(ids)
                .map(([key, id]) => `${key}=${id}`)
                .join(
                    ' ',
                )} start=${request.start_date} end=${request.end_date} (${seconds}s)`,
        );
        try {
            return await this.fetch(`${this.runtimeBase}/tracking`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    ...ids,
                    mode: 'live',
                    start_date: request.start_date,
                    end_date: request.end_date,
                }),
            });
        } catch (error) {
            console.error('Could not publish tracking', error);
            return null;
        }
    }
}
